"use client";

import UserAvatar from "components/User/UserAvatar";
import { UserTooltip } from "components/ui/v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import type { ReactNode } from "react";
import { IUser } from "types/User.types";
import { formatUserDisplayName } from "utils/string";

export interface ExplorePersonRowV2Props {
  user: Partial<IUser> & { id?: string; name?: string; lastname?: string; email?: string };
  /** Línea 2 opcional (email · rol, fecha, etc.). */
  secondaryLine?: string;
  /** Contenido alineado a la derecha (fecha, acción, badge). */
  trailing?: ReactNode;
  avatarSize?: number;
  avatarOverlay?: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export function ExplorePersonRowV2({
  user,
  secondaryLine,
  trailing,
  avatarSize = 32,
  avatarOverlay,
  href,
  onClick,
  className = "",
}: ExplorePersonRowV2Props) {
  const { user: storeUser } = useUserStoreContext((store) => ({
    user: store.user,
  }));
  const vendorId = storeUser?.selectedVendor?.id;
  const displayName = formatUserDisplayName(user.name, user.lastname);
  const profileHref =
    href ?? (user.id ? `/profile/${user.id}?vendorId=${vendorId ?? ""}` : undefined);

  const textBlock = (
    <div className="min-w-0 flex-1 text-left leading-tight">
      {profileHref && !onClick ? (
        <a
          href={profileHref}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-[13px] font-medium leading-snug capitalize text-slate-900 dark:text-zinc-50"
        >
          {displayName.toLowerCase()}
        </a>
      ) : (
        <p className="truncate text-[13px] font-medium leading-snug capitalize text-slate-900 dark:text-zinc-50">
          {displayName.toLowerCase()}
        </p>
      )}
      {secondaryLine ? (
        <p className="truncate text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
          {secondaryLine}
        </p>
      ) : user.email ? (
        <p className="truncate text-[9px] leading-snug text-slate-500 dark:text-zinc-400">
          {user.email}
        </p>
      ) : null}
    </div>
  );

  const row = (
    <div
      className={`flex items-center gap-2 rounded-lg px-1 py-1.5 sm:px-0.5 ${className}`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="relative shrink-0">
        <UserAvatar user={user as IUser} size={avatarSize} />
        {avatarOverlay}
      </div>
      <UserTooltip user={user as Partial<IUser>} side="top">
        {textBlock}
      </UserTooltip>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );

  return row;
}

export default ExplorePersonRowV2;
